import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * ``ProtectedRoute`` — gate a route behind an authed principal.
 *
 * Options:
 * - ``roles``: allow-list of role names — 403 otherwise
 * - ``requirePlatformAdmin``: gate on identity.platform_role === "platform_admin"
 * - ``allowTempToken``: allow render when the ONLY thing the client has is
 *   the multi-tenant temp_token (used for /app/select-tenant). The page
 *   itself validates the token from sessionStorage.
 */
export function ProtectedRoute({ children, roles, requirePlatformAdmin, allowTempToken }) {
  const { user, booting, hasRole, isPlatformAdmin } = useAuth();
  const location = useLocation();
  if (booting) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-secondary border-t-transparent" />
      </div>
    );
  }
  if (!user) {
    if (allowTempToken) return children;
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (requirePlatformAdmin && !isPlatformAdmin) {
    return (
      <div className="flex h-screen items-center justify-center" data-testid="forbidden-page">
        <div className="max-w-md rounded-xl border p-8 text-center">
          <div className="mb-2 text-3xl font-display">403</div>
          <p className="text-muted-foreground">Platform admin access required.</p>
        </div>
      </div>
    );
  }
  if (roles && !hasRole(roles)) {
    return (
      <div className="flex h-screen items-center justify-center" data-testid="forbidden-page">
        <div className="max-w-md rounded-xl border p-8 text-center">
          <div className="mb-2 text-3xl font-display">403</div>
          <p className="text-muted-foreground">You don't have access to this section.</p>
        </div>
      </div>
    );
  }
  return children;
}
